import { TOrderFieldQuery } from 'types/orderObjects/orderObject';

import { OrderObjectResp } from '@api/responseModels/orderObjects/orderObjectResponse';
import { OrderObjectRestoreResp } from '@api/responseModels/orderObjects/orderObjectRestoreResponse';
import { OrderObjectStatisticResp } from '@api/responseModels/orderObjects/orderObjectStatisticResponse';
import { OrderObjectsCreateResp } from '@api/responseModels/orderObjects/orderObjectsCreateResponse';
import { OrderObjectsResp } from '@api/responseModels/orderObjects/orderObjectsResponse';
import { RequestResult } from '@api/responseModels/requestResult';

import { CRMAPIBase } from '../crmApiBase';

class OrderObjectsGroup extends CRMAPIBase {
    async createOrderObject({
        name,
        order_object_category_id,
        ownerable_type,
        ownerable_id,
        fields,
    }: {
        name: string;
        order_object_category_id: number;
        ownerable_type:
            | 'App\\Models\\Api\\V1\\User'
            | 'App\\Models\\Api\\V1\\CustomerProfile'
            | 'App\\Models\\Api\\V1\\Crm';
        ownerable_id?: number;
        fields?: {
            name: string;
            value: string | number | { id?: number; value?: string };
        }[];
    }): Promise<RequestResult<OrderObjectsCreateResp>> {
        const result = await this.post<OrderObjectsCreateResp>(`order-objects`, {
            name,
            order_object_category_id,
            ownerable_type,
            ownerable_id,
            fields,
        });
        return result;
    }

    async getOrderObject(order_object_id: number): Promise<RequestResult<OrderObjectResp>> {
        const result = await this.get<OrderObjectResp>(`order-objects/${order_object_id}`, {});
        return result;
    }

    async updateOrderObject({
        order_object_id,
        name,
        fields,
    }: {
        order_object_id: number;
        name: string;
        category_id: number;
        fields: {
            id: number;
            name: string;
            category_id?: number;
            value: {
                id?: number;
                value: string;
            };
        }[];
    }): Promise<RequestResult<OrderObjectResp>> {
        const result = await this.put<OrderObjectResp>(`order-objects/${order_object_id}`, {
            name,
            fields,
        });
        return result;
    }

    async getOrderObjects(
        crm_id?: number,
        customer_profile_id?: number,
        page = 1,
        filters?: { is_draft?: 'only' | 'all' | 'null' }
    ): Promise<RequestResult<OrderObjectsResp>> {
        const result = await this.get<OrderObjectsResp>(`order-objects`, {
            // crm_id,
            customer_profile_id,
            page,
            sort_direction: 'desc',
            ...(filters ? { filters } : {}),
        });
        return result;
    }

    async deleteOrderObject(
        order_object_id: number,
        crm_id: number
    ): Promise<RequestResult<OrderObjectsResp>> {
        const result = await this.delete<OrderObjectsResp>(`order-objects/${order_object_id}`, {});
        return result;
    }

    async restoreOrderObject(
        order_object_id: number
    ): Promise<RequestResult<OrderObjectRestoreResp>> {
        const result = await this.post<OrderObjectRestoreResp>(
            `order-objects/${order_object_id}/restore`
        );
        return result;
    }

    async syncOrderFieldsOrderObject(
        crm_id: number,
        order_object_id: number,
        order_fields: TOrderFieldQuery[]
    ): Promise<RequestResult<OrderObjectResp>> {
        const result = await this.post<OrderObjectResp>(
            `order-objects/${order_object_id}/sync-fields`,
            {
                order_fields,
            }
        );
        return result;
    }

    async adminOrderObjects(page: number): Promise<RequestResult<any>> {
        const result = await this.get<any>(`admin/order-objects`, {
            page,
        });
        return result;
    }

    async getAdminOrderObject(id: number): Promise<RequestResult<any>> {
        const result = await this.get<any>(`admin/order-objects/${id}`);
        return result;
    }

    async createAdminOrderObjects({
        name,
        order_object_category_id,
        fields,
    }: {
        name: string;
        order_object_category_id: number;
        fields?: {
            name: string;
            value: string | number | { id?: number; value?: string };
        }[];
    }): Promise<RequestResult<OrderObjectsCreateResp>> {
        const result = await this.post<OrderObjectsCreateResp>(`admin/order-objects`, {
            name,
            order_object_category_id,
            fields,
        });
        return result;
    }

    async orderObjectCategories({ crm_id }): Promise<RequestResult<any>> {
        const result = await this.get<any>(`order-object-categories`, {
            crm_id,
        });
        return result;
    }

    async getOrderObjectStatistic({
        id,
        entity,
    }: {
        id: string;
        entity: 'products' | 'services' | 'tasks';
    }): Promise<RequestResult<OrderObjectStatisticResp>> {
        const result = await this.get<OrderObjectStatisticResp>(
            `/admin/order-objects/${id}/getStatistic`,
            {
                entity,
            }
        );
        return result;
    }

    async getOrderObjectMeta({
        id,
        entity,
    }: {
        id: string;
        entity: 'products' | 'services' | 'tasks';
    }): Promise<RequestResult<OrderObjectStatisticResp>> {
        const result = await this.get<OrderObjectStatisticResp>(
            `/admin/order-objects/${id}/getMetaDataForEntity`,
            {
                entity,
                entity_id: id,
            }
        );
        return result;
    }
    async getOrderObjectRelatedEntities({
        id,
        entity,
        related,
    }: {
        id: string;
        entity: 'products' | 'services' | 'tasks';
        related: 'products' | 'services' | 'tasks';
    }): Promise<RequestResult<OrderObjectStatisticResp>> {
        const result = await this.get<OrderObjectStatisticResp>(
            `/admin/order-objects/${id}/getRelatedEntities`,
            {
                entity,
                entity_id: id,
                related,
            }
        );
        return result;
    }
}
export { OrderObjectsGroup };
